import _ from 'lodash';
import { Entity, ctx as L20NContext } from '@sketchpixy/rubix/lib/L20n';
import * as FileSaver from 'file-saver';
import moment from 'moment';
import React from 'react';
import * as XLSX from 'xlsx';
import * as CustomTablesAPI from '../../_config/customTablesAPI';
import * as formatter from '../../_config/formatter';
import { CUSTOM_TABLE_LIMITS, EXPORT_FORMATS, SUBSCRIPTION_TYPES, EXTERNAL_CORE_TABLE_TYPES } from '../../_config/consts';
import * as ModalActions from './modal.actions';
import {
  ADD_CUSTOM_TABLE_ROW,
  ADD_FIELD_TO_TABLE,
  APPEND_CUSTOM_TABLES,
  DELETE_CUSTOM_TABLE_ROW,
  DELETE_FIELD,
  RESET_CUSTOM_TABLES_DATA,
  RESET_CUSTOM_TABLES_FILTERS,
  RESET_CUSTOM_TABLE_PAGINATION_DATA,
  SAVE_CUSTOM_TABLES,
  SELECT_CUSTOM_TABLE,
  SET_CUSTOM_TABLES_FILTER,
  SET_CUSTOM_TABLES_OPERATIONAL_MODE,
  SET_CUSTOM_TABLES_PAGINATION_DATA,
  SET_CUSTOM_TABLE_ROWS,
  SET_CUSTOM_TABLE_ROWS_PAGINATION,
  SET_SELECTED_FIELD,
  SET_SELECTED_ROW,
  UPDATE_CUSTOM_TABLE,
  UPDATE_CUSTOM_TABLE_ROW,
  UPDATE_FIELD,
  RESET_CUSTOM_TABLE_ROWS_DATA,
  UPDATE_CUSTOM_TABLE_SNIPPET_ON_STATE,
  SAVE_CUSTOM_TABLE_LIMITS,
} from './actionTypes/customTables';

export function setCustomTablesOperationalMode(value) {
  return {
    type: SET_CUSTOM_TABLES_OPERATIONAL_MODE,
    value,
  };
}

export function saveCustomTables(customTables) {
  return {
    type: SAVE_CUSTOM_TABLES,
    customTables,
  };
}

export function appendCustomTables(customTables) {
  return {
    type: APPEND_CUSTOM_TABLES,
    customTables,
  };
}

export function saveCustomTablesPaginationData(pagination) {
  return {
    type: SET_CUSTOM_TABLES_PAGINATION_DATA,
    pagination,
  };
}

export function setCustomTablesFilter(field, value) {
  return {
    type: SET_CUSTOM_TABLES_FILTER,
    field,
    value,
  };
}

export function setSelectedCustomTable(customTable) {
  return {
    type: SELECT_CUSTOM_TABLE,
    customTable,
  };
}

export function updateCustomTableOnState(customTable) {
  return {
    type: UPDATE_CUSTOM_TABLE,
    customTable,
  };
}

export function resetCustomTablesData() {
  return {
    type: RESET_CUSTOM_TABLES_DATA,
  };
}

export function resetCustomTablesFilters() {
  return {
    type: RESET_CUSTOM_TABLES_FILTERS,
  };
}

export function resetCustomTablesPagination() {
  return {
    type: RESET_CUSTOM_TABLE_PAGINATION_DATA,
  };
}

export function addFieldToTableAction(field) {
  return {
    type: ADD_FIELD_TO_TABLE,
    field,
  };
}

export function updateFieldAction(field) {
  return {
    type: UPDATE_FIELD,
    field,
  };
}

export function deleteFieldAction(fieldId) {
  return {
    type: DELETE_FIELD,
    fieldId,
  };
}

export function setSelectedField(field) {
  return {
    type: SET_SELECTED_FIELD,
    field,
  };
}

// Row Actions
export function setCustomTableRows(rows) {
  return {
    type: SET_CUSTOM_TABLE_ROWS,
    rows,
  };
}

export function addCustomTableRowToState(row) {
  return {
    type: ADD_CUSTOM_TABLE_ROW,
    row,
  };
}

export function updateCustomTableRowInState(row) {
  return {
    type: UPDATE_CUSTOM_TABLE_ROW,
    row,
  };
}

export function deleteCustomTableRowFromState(rowId) {
  return {
    type: DELETE_CUSTOM_TABLE_ROW,
    rowId,
  };
}

export function setSelectedRow(row) {
  return {
    type: SET_SELECTED_ROW,
    row,
  };
}

export function setCustomTableRowsPagination(pagination) {
  return {
    type: SET_CUSTOM_TABLE_ROWS_PAGINATION,
    pagination,
  };
}

export function resetCustomTableRowsData() {
  return {
    type: RESET_CUSTOM_TABLE_ROWS_DATA,
  };
}

export function updateCustomTableSnippetOnState(customSnippet) {
  return {
    type: UPDATE_CUSTOM_TABLE_SNIPPET_ON_STATE,
    customSnippet,
  };
}

// Thunk actions
export function fetchCustomTables(page = 0, pageSize = 50) {
  return async (dispatch, getState) => {
    try {
      const filters = getState().customTables.data.filters;
      const params = {
        page,
        pageSize,
        ...filters,
      };
      const customTablesResponse = await CustomTablesAPI.getCustomTables(params);
      if (customTablesResponse && customTablesResponse.data && customTablesResponse.data.content) {
        dispatch(saveCustomTables(customTablesResponse.data.content));
        dispatch(saveCustomTablesPaginationData(_.omit(customTablesResponse.data, 'content')));
        return customTablesResponse.data.content;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}


export function fetchCustomTablesWithDetails(page = 0, pageSize = 50) {
  return async (dispatch, getState) => {
    try {
      const filters = getState().customTables.data.filters;
        const params = {
          page,
          pageSize,
          ...filters,
        };
      const customTablesResponse = await CustomTablesAPI.getCustomTables(params);
      if (customTablesResponse && customTablesResponse.data && customTablesResponse.data.content) {
        const customTables = customTablesResponse.data.content || [];
        const customTablesWithFields = [];
        for (const customTable of customTables) {
          try {
            const customTableDetails = await dispatch(getCustomTableDetails(customTable.id));
            customTablesWithFields.push(customTableDetails);
          } catch (error) {
            console.error(`Error fetching fields for table ${customTable.id}:`, error);
            customTablesWithFields.push({
              ...customTable,
              fields: []
            });
          }
        }
        dispatch(saveCustomTables(customTablesWithFields));
        dispatch(saveCustomTablesPaginationData(_.omit(customTablesResponse.data, 'content')));
        return customTablesWithFields;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  }
}

export function createCustomTable(customTableData) {
  return async (dispatch) => {
    try {
      const createResponse = await CustomTablesAPI.createCustomTable(customTableData);
      if (createResponse && createResponse.data) {
        return createResponse.data;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

export function updateCustomTable(customTableId, customTableData) {
  return async (dispatch) => {
    try {
      const updateResponse = await CustomTablesAPI.updateCustomTable(customTableId, customTableData);
      if (updateResponse && updateResponse.data) {
        dispatch(updateCustomTableOnState(updateResponse.data));
        return updateResponse.data;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

export function deleteCustomTable(customTableId) {
  return async (dispatch) => {
    try {
      const deleteResponse = await CustomTablesAPI.deleteCustomTable(customTableId);
      return;
    } catch (error) {
      throw error;
    }
  };
}

export function getCustomTableDetails(customTableId) {
  return async (dispatch) => {
    try {
      const getDetailsResponse = await CustomTablesAPI.getCustomTableDetails(customTableId);
      if (getDetailsResponse && getDetailsResponse.data) {
        return getDetailsResponse.data;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  }
}

export function createCustomTableField(customTableId, fieldData) {
  return async (dispatch) => {
    try {
      const addFieldResponse = await CustomTablesAPI.createCustomTableField(customTableId, fieldData);
      if (addFieldResponse && addFieldResponse.data) {
        dispatch(addFieldToTableAction(addFieldResponse.data));
        return addFieldResponse.data;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

export function updateCustomTableField(customTableId, fieldId, fieldData) {
  return async (dispatch) => {
    try {
      const updateFieldResponse = await CustomTablesAPI.updateField(customTableId, fieldId, fieldData);
      if (updateFieldResponse && updateFieldResponse.data) {
        dispatch(updateFieldAction(updateFieldResponse.data));
        return updateFieldResponse.data;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

export function deleteCustomTableField(customTableId, fieldId) {
  return async (dispatch) => {
    try {
      const deleteFieldResponse = await CustomTablesAPI.deleteField(customTableId, fieldId);
      if (deleteFieldResponse) {
        dispatch(deleteFieldAction(fieldId));
        return deleteFieldResponse;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

function formatDateForBackend(dateValue) {
  if (!dateValue) return null;
  
  if (dateValue && typeof dateValue === 'object' && dateValue._isAMomentObject) {
    return dateValue.format('YYYY-MM-DD');
  }
  else if (dateValue instanceof Date) {
    return dateValue.toISOString().split('T')[0];
  }
  else if (typeof dateValue === 'string' && dateValue.includes('T')) {
    return dateValue.split('T')[0];
  }
  else if (typeof dateValue === 'string' && dateValue.trim()) {
    const trimmed = dateValue.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }
    const parsed = new Date(trimmed);
    if (!isNaN(parsed.getTime())) {
      return parsed.toISOString().split('T')[0];
    }
  }
  
  return null;
}

function convertFiltersToBackendParams(filters, selectedCustomTable) {
  const params = {};
  
  if (!filters || !selectedCustomTable || !selectedCustomTable.fields) {
    return params;
  }

  selectedCustomTable.fields.forEach(field => {
    const fieldName = field.name;
    const fieldType = field.type;
    const filterValue = filters[fieldName];

    if (!filterValue) return;

    if (fieldType === 'text') {
      if (typeof filterValue === 'string' && filterValue.trim()) {
        params[`${fieldName}__like`] = filterValue.trim();
      }
    }
    else if (fieldType === 'integerNumber' || fieldType === 'decimalNumber' || fieldType === 'monetary') {
      if (typeof filterValue === 'object') {
        if (filterValue.min && filterValue.min.toString().trim()) {
          params[`${fieldName}__gte`] = filterValue.min.toString().trim();
        }
        if (filterValue.max && filterValue.max.toString().trim()) {
          params[`${fieldName}__lte`] = filterValue.max.toString().trim();
        }
      }
    } else if (fieldType === 'externalReference') {
      params[`${fieldName}`] = filterValue;
    }
    else if (fieldType === 'date') {
      if (typeof filterValue === 'object') {
        if (filterValue.min) {
          const formattedMinDate = formatDateForBackend(filterValue.min);
          if (formattedMinDate) {
            params[`${fieldName}__gte`] = formattedMinDate;
          }
        }
        if (filterValue.max) {
          const formattedMaxDate = formatDateForBackend(filterValue.max);
          if (formattedMaxDate) {
            params[`${fieldName}__lte`] = formattedMaxDate;
          }
        }
      }
    }
  });
  return params;
}

async function enrichRowsWithExternalData(rows, fields) {
  const externalRefFields = _.filter(fields, field => field.type === 'externalReference' && field.externalCoreTable);
  
  if (externalRefFields.length === 0) {
    return rows;
  }

  const entityFetchMap = {};
  
  externalRefFields.forEach(field => {
    const entityType = field.externalCoreTable;
    const entityIds = [];
    
    rows.forEach(row => {
      const fieldValue = row.data && row.data[field.name];
      if (fieldValue && !entityIds.includes(fieldValue)) {
        entityIds.push(fieldValue);
      }
    });
    
    if (entityIds.length > 0) {
      entityFetchMap[field.name] = {
        entityType,
        entityIds,
      };
    }
  });

  const entityDataMap = {};
  
  for (const fieldName in entityFetchMap) {
    const { entityType, entityIds } = entityFetchMap[fieldName];
    try {
      const entities = await CustomTablesAPI.fetchExternalEntities(entityType, entityIds);
      entityDataMap[fieldName] = entities;
    } catch (error) {
      console.error(`Error fetching entities for field ${fieldName}:`, error);
      entityDataMap[fieldName] = {};
    }
  }

  const enrichedRows = rows.map(row => {
    const enrichedData = { ...row.data };
    
    externalRefFields.forEach(field => {
      const fieldValue = row.data && row.data[field.name];
      if (fieldValue) {
        const entityData = entityDataMap[field.name] && entityDataMap[field.name][fieldValue];
        
        if (entityData) {
          let displayName = '';
          
          if (field.externalCoreTable === EXTERNAL_CORE_TABLE_TYPES.USER) {
            displayName = `${entityData.firstname || ''} ${entityData.lastname || ''}`.trim();
            if (entityData.email) {
              displayName = displayName ? `${displayName} (${entityData.email})` : entityData.email;
            }
          } else if (field.externalCoreTable === EXTERNAL_CORE_TABLE_TYPES.SMART_LOCK) {
            displayName = entityData.name || entityData.deviceId || `Lock ${fieldValue}`;
          }
          
          enrichedData[field.name] = {
            id: fieldValue,
            displayName: displayName || fieldValue,
            entityData,
          };
        } else {
          enrichedData[field.name] = {
            id: fieldValue,
            displayName: `ID: ${fieldValue} (not found)`,
            entityData: null,
          };
        }
      }
    });
    
    return {
      ...row,
      data: enrichedData,
    };
  });
  
  return enrichedRows;
}

export function fetchCustomTableRows(customTableId, page = 0, size = 50, filters = {}, selectedCustomTable = null) {
  return async (dispatch) => {
    try {
      const filterParams = convertFiltersToBackendParams(filters, selectedCustomTable);
      const params = {
        page,
        pageSize: size,
        ...filterParams
      };
      const response = await CustomTablesAPI.getCustomTableRows(customTableId, params);
      if (response && response.data) {
        let rowsToDispatch = response.data.content;

        if (selectedCustomTable && selectedCustomTable.fields && _.find(selectedCustomTable.fields, field => field.type === 'externalReference')) {
          try {
            rowsToDispatch = await enrichRowsWithExternalData(response.data.content, selectedCustomTable.fields);
          } catch (error) {
            console.error('Error enriching rows with external data:', error);
          }
        }
        
        dispatch(setCustomTableRows(rowsToDispatch));
        dispatch(setCustomTableRowsPagination({
          currentPage: response.data.number || page,
          totalPages: response.data.totalPages || 0,
          size: response.data.size || size,
          totalElements: response.data.totalElements || 0,
        }));
        return rowsToDispatch;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

export function createCustomTableRow(customTableId, rowData) {
  return async (dispatch) => {
    try {
      const formattedRowData = formatter.formatOutputData(formatter.CUSTOM_TABLE_ROW_DATA, rowData);
      const response = await CustomTablesAPI.createCustomTableRow(customTableId, formattedRowData);
      if (response && response.data) {
        dispatch(addCustomTableRowToState(response.data));
        return response.data;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

export function updateCustomTableRow(customTableId, rowId, rowData) {
  return async (dispatch) => {
    try {
      const response = await CustomTablesAPI.updateCustomTableRow(customTableId, rowId, rowData);
      if (response && response.data) {
        dispatch(updateCustomTableRowInState(response.data));
        return response.data;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

export function deleteCustomTableRow(customTableId, rowId) {
  return async (dispatch) => {
    try {
      const response = await CustomTablesAPI.deleteCustomTableRow(customTableId, rowId);
      if (response) {
        dispatch(deleteCustomTableRowFromState(rowId));
        return response;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

export function getTableRowById(customTableId, rowId) {
  return async (dispatch) => {
    try {
      const response = await CustomTablesAPI.getCustomTableRowById(customTableId, rowId);
      if (response && response.data) {
        dispatch(setSelectedRow(response.data));
        return response.data;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

export function exportCustomTableRows(customTableId, selectedCustomTable, filters = {}, format = EXPORT_FORMATS.CSV) {
  return async (dispatch, getState) => {
    try {
      const filterParams = convertFiltersToBackendParams(filters, selectedCustomTable);
      const params = {
        page: 0,
        pageSize: 10000,
        ...filterParams
      };
      
      const response = await CustomTablesAPI.getCustomTableRows(customTableId, params);
      const rows = response.data.content || [];
      
      const exportData = [];
      _.each(rows, (row) => {
        const rowData = {};
        
        _.each(selectedCustomTable.fields, (field) => {
          let value = row.data && row.data[field.name] ? row.data[field.name] : '---';
          
          if (field.type === 'date' && value !== '---') {
            value = moment(value).format('LLL');
          } else if (field.type === 'monetary' && value !== '---') {
            value = Number(value) || 0;
          } else if ((field.type === 'integerNumber' || field.type === 'decimalNumber') && value !== '---') {
            value = Number(value);
          }
          
          rowData[field.name] = value;
        });
        
        exportData.push(rowData);
      });
      
      if (exportData.length === 0) {
        dispatch(ModalActions.showModal({
          modalType: 'ERROR_ALERT',
          modalProps: {
            message: (<h6 className="snack-title"><Entity entity="noDataToExport" /></h6>),
          },
        }));
        return;
      }
      
      const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
      const fileExtension = format.toLowerCase();
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = { Sheets: { [selectedCustomTable.name]: ws }, SheetNames: [selectedCustomTable.name] };
      const excelBuffer = XLSX.write(wb, { bookType: format.toLowerCase(), type: 'array' });
      const data = new Blob([excelBuffer], { type: fileType });
      FileSaver.saveAs(data, `${selectedCustomTable.name}_Export.${fileExtension}`);
      
      return exportData;
    } catch (error) {
      dispatch(ModalActions.showModal({
        modalType: 'ERROR_ALERT',
        modalProps: {
          message: (<h6 className="snack-title"><Entity entity="errorCreatingExport" /></h6>),
        },
      }));
      throw new Error(error);
    }
  };
}

export function saveCustomTableLimits(limits) {
  return {
    type: SAVE_CUSTOM_TABLE_LIMITS,
    limits,
  };
}

export function getCustomTableSubscriptionLimits() {
  return (dispatch, getState) => {
    const { user: { license } } = getState();
    const subscriptions = license && license.subscriptions;
    let limits = null;
    if (!_.isEmpty(subscriptions)) {
      // Check in priority order: CUSTOM_TABLE (unlimited) > CUSTOM_TABLE_MEDIUM > CUSTOM_TABLE_SMALL
      if (_.find(subscriptions, subscription => subscription.resourceType === SUBSCRIPTION_TYPES.CUSTOM_TABLES)) {
        limits = CUSTOM_TABLE_LIMITS[SUBSCRIPTION_TYPES.CUSTOM_TABLES];
      } else if (_.find(subscriptions, subscription => subscription.resourceType === SUBSCRIPTION_TYPES.CUSTOM_TABLES_MEDIUM)) {
        limits = CUSTOM_TABLE_LIMITS[SUBSCRIPTION_TYPES.CUSTOM_TABLES_MEDIUM];
      } else if (_.find(subscriptions, subscription => subscription.resourceType === SUBSCRIPTION_TYPES.CUSTOM_TABLES_SMALL)) {
        limits = CUSTOM_TABLE_LIMITS[SUBSCRIPTION_TYPES.CUSTOM_TABLES_SMALL];
      }
    }
    
    // If no custom table subscription found, use most restrictive (SMALL)
    if (!limits) {
      limits = CUSTOM_TABLE_LIMITS[SUBSCRIPTION_TYPES.CUSTOM_TABLES_SMALL];
    }
    
    dispatch(saveCustomTableLimits(limits));
    return limits;
  };
}

